import React, { createContext, useContext, useMemo } from 'react';
import { Density, DensityProviderProps, DensityToControlSize } from '../../../types/DesignSystem/density';
import { ControlSize, LegacyControlSize, resolveControlSize } from '../controlSize';

/**
 * DensityProvider (§ 313) — density as a region.
 *
 * See `types/DesignSystem/density.ts` for why the vocabulary is `TableDensity`'s and
 * not a new one.
 *
 * **The default is `'default'`, so a tree with no provider renders exactly what it
 * rendered before this existed.** That is the whole safety argument for touching ten
 * shared controls in one change: `useControlSize(undefined)` with no provider returns
 * `'md'`, which is the literal default every one of them had at its destructure.
 * `densityProvider.test.tsx` asserts that for each of the ten, because it is the
 * property that makes this additive rather than an app-wide resize.
 *
 * **Renders no DOM.** A wrapper element would change layout — a provider around a flex
 * item would become the flex item. It is context only; a spec asserts the resulting
 * control heights, not a `data-density` attribute that does not exist.
 */

/**
 * § 245's three row heights, mapped onto `controlSize.ts`'s three control heights.
 * Exported so the showcase and the tests can show the correspondence rather than
 * restate it.
 */
export const DENSITY_CONTROL_SIZE: DensityToControlSize = {
  compact: 'sm',
  default: 'md',
  comfortable: 'lg',
};

/**
 * `'default'` is the context default, NOT `undefined`. A nullable context would make
 * every consumer write the same `?? 'default'` fallback, and one of them would
 * eventually write `?? 'compact'`.
 */
const DensityContext = createContext<Density>('default');

const DensityProvider = ({ density, children }: DensityProviderProps) => {
  const inherited = useContext(DensityContext);
  /* `density` omitted means INHERIT, so a nested provider that only wants to change
     one branch does not have to restate the outer value. */
  const value = density ?? inherited;
  /* The value is a string, so memoising the provider's value is not about identity —
     it is about not re-rendering every control below on an unrelated parent render. */
  const memo = useMemo(() => value, [value]);
  return <DensityContext.Provider value={memo}>{children}</DensityContext.Provider>;
};

/** The density of the current region. `'default'` when nothing has set one. */
export const useDensity = (): Density => useContext(DensityContext);

/**
 * The control-grid rung this control should render at.
 *
 * **An explicit `size` prop always wins.** A caller who wrote `size="lg"` meant it,
 * and a surrounding compact region must not quietly override them — density is a
 * default, never an instruction.
 *
 * **`undefined` means "inherit" here, and that is a deliberate reversal.**
 * `controlSize.ts` documents the opposite rule — `resolveControlSize` takes a REQUIRED
 * parameter precisely so that a stray `undefined` cannot silently resolve to `'md'`,
 * the trap that hid a real bug in `ErrorState`'s `role`. That rule stands for that
 * function. Here `undefined` is the whole mechanism: it is how a control says "I was
 * not told, so ask the region". The difference is that the fallback is now a VALUE
 * FROM CONTEXT rather than a hardcoded literal, so a caller passing `undefined`
 * explicitly and a caller omitting the prop get the same answer — which is the
 * behaviour they both expect.
 */
export const useControlSize = (size?: ControlSize | LegacyControlSize): ControlSize => {
  const density = useDensity();
  return size === undefined ? DENSITY_CONTROL_SIZE[density] : resolveControlSize(size);
};

export default DensityProvider;
